"use client";

import React, { useState, useEffect, useRef } from "react";
import { allowCheckout } from "@/app/_module/utils/nativeCheckoutGuard";
import { useForm, FormProvider } from "react-hook-form";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { useToast } from "@/app/_module/hooks/use-toast";
import { cn } from "@/app/_module/lib/utils";
import { A2PVerificationData, WizardStep, A2PVerificationStatus, RECOMMENDED_USE_CASE } from "@/app/_module/types/a2p.types";
import { StatusPanel } from "./overview/StatusPanel";
import { ReviewSteps } from "./overview/ReviewSteps";
import { Panel, focusRing } from "./overview/primitives";
import { clearDraft, readDraft, writeDraft } from "./overview/draft";
import { a2pService } from "@/app/_module/services/a2pService";
import { a2pPaymentService } from "@/app/_module/services/a2pPaymentService";
import { useUserContext } from "@/app/_module/store/contexts/userContext";

// Step Components
import WizardProgress from "./WizardProgress";
import Step0RegistrationTypeSelect, { type RegistrationType } from "./Step0RegistrationTypeSelect";
import Step1EINCheck from "./Step1EINCheck";
import StepCp575Upload from "./StepCp575Upload";
import Step2BusinessInfo from "./Step2BusinessInfo";
import Step2SoleProprietorInfo from "./Step2SoleProprietorInfo";
import Step3CampaignInfo from "./Step3CampaignInfo";
import StepTextingNumber from "./StepTextingNumber";
import PaymentStep from "./PaymentStep";

// Helper function to normalize phone number to +1xxxxxxxxxx format
const normalizePhoneForBackend = (phone: string): string => {
  if (!phone) return '';
  // Extract only digits
  const digits = phone.replace(/\D/g, '');
  // Remove leading 1 if present (handles +1xxxxxxxxxx or 1xxxxxxxxxx)
  // 11 digits starting with 1 = country code included
  const normalizedDigits = digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
  // Limit to 10 digits and add +1 prefix
  const limitedDigits = normalizedDigits.slice(0, 10);
  if (limitedDigits.length === 10) {
    return `+1${limitedDigits}`;
  }
  // Return original if not complete phone number
  return phone;
};

interface A2PWizardProps {
  onComplete?: (verification: A2PVerificationStatus) => void;
  onCancel?: () => void;
  /** Pick up the progress saved in this browser (see overview/draft). */
  resume?: boolean;
}

export default function A2PWizard({ onComplete, onCancel, resume = false }: A2PWizardProps) {
  const { toast } = useToast();
  const { user: userProfile } = useUserContext();

  // Only an owner/admin of the CURRENT agency may register on its behalf. The
  // backend re-checks this — 261 users hold role 'owner', mostly over their own
  // one-person sub-agency, so this is a UI affordance, not the gate.
  const currentAgency = (userProfile as any)?.currentAgency;
  const canRegisterForAgency = ['owner', 'admin'].includes(currentAgency?.role ?? '');
  const [registerForAgency, setRegisterForAgency] = useState(false);
  const [coversDownline, setCoversDownline] = useState(true);
  const [agencyRiskAccepted, setAgencyRiskAccepted] = useState(false);

  // Start at Step 0 (registration type selection)
  const [currentStep, setCurrentStep] = useState<WizardStep>(0 as WizardStep);
  const [completedSteps, setCompletedSteps] = useState<WizardStep[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [verificationResult, setVerificationResult] = useState<A2PVerificationStatus | null>(null);
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Registration type selection (Step 0)
  const [registrationType, setRegistrationType] = useState<RegistrationType | null>(null);

  // Registering for an agency means one bad sender can take the whole downline
  // offline, so the acknowledgement is required before they can move on. The
  // backend rejects a submission without it regardless. Only Low Volume Standard
  // can cover an agency (and only it sends these fields): ticking the agency box
  // on LVS and then switching to sole prop used to leave Continue disabled,
  // waiting on a checkbox that was no longer on screen.
  const agencyAckMissing =
    registrationType === 'low_volume_standard' && registerForAgency && !agencyRiskAccepted;

  // Ref to prevent double-submit (synchronous check before async setState)
  const isSubmittingRef = useRef(false);

  // Generate pre-populated values based on user data
  // NOTE: We intentionally do NOT pre-populate businessName with agency name
  // because the user must enter their exact legal business name as registered with IRS
  const getPrePopulatedValues = () => {
    const userName = userProfile?.user?.name || '';
    const userEmail = userProfile?.user?.email || '';
    const userPhone = userProfile?.user?.contactPhone || userProfile?.user?.phone || '';

    // Don't pre-populate business name - user must enter exact IRS-registered name
    // This prevents confusion where users submit with agency name instead of legal name

    // Generate campaign name placeholder (user should update with their legal business name)
    const campaignName = userName
      ? `${userName} - Insurance Client Communications`
      : 'Insurance Client Communications';

    // Generate insurance-specific campaign description placeholder
    const campaignDescription = 'We use SMS messaging to communicate with our existing insurance clients. This includes appointment reminders and confirmations, policy update notifications, and follow-up communications. All recipients have provided verbal consent to receive these messages and can reply STOP at any time to unsubscribe.';

    // Split user name into first/last for authorized rep
    const nameParts = userName.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    return {
      hasEIN: false,
      ein: "",
      businessName: "", // Empty - user must enter exact IRS-registered legal name
      businessType: "Sole Proprietorship", // Default for most solo agents
      businessEmail: userEmail,
      businessPhone: normalizePhoneForBackend(userPhone),
      website: "https://insuracentral.com", // Locked to InsuraCentral for A2P compliance
      authorizedRepFirstName: firstName, // Pre-populate with user's first name
      authorizedRepLastName: lastName, // Pre-populate with user's last name
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "United States",
      campaignName: campaignName,
      campaignDescription: campaignDescription,
      useCase: RECOMMENDED_USE_CASE, // Pre-select recommended use case for insurance agents
      sampleMessages: [
        { message: `Hi Sarah, this is ${userName || 'your agent'}. Just a reminder about our appointment tomorrow at 2:00 PM. Please call me if you need to reschedule. Reply STOP to opt out. Msg & data rates may apply.` },
        { message: `Hi Michael, this is ${userName || 'your agent'}. I wanted to follow up on the life insurance options we discussed. When would be a good time for a quick call? Reply STOP to opt out. Msg & data rates may apply.` },
      ],
      monthlyVolume: 6000, // Low-Volume Standard: 6,000 message segments per day
    };
  };

  // Initialize form with pre-populated values based on user data
  const methods = useForm<A2PVerificationData>({
    mode: "onChange",
    defaultValues: getPrePopulatedValues(),
  });

  const { handleSubmit, trigger, getValues, watch, reset } = methods;
  const hasEIN = watch("hasEIN");

  // Draft values restored on resume; kept so the profile reset below doesn't wipe them.
  const restoredRef = useRef<Record<string, any> | null>(null);
  const submittedRef = useRef(false);
  const saveTimer = useRef<number | undefined>(undefined);
  const userId = userProfile?.user?.id;

  // Update form when user data loads
  useEffect(() => {
    if (userProfile?.user) {
      const prePopulated = getPrePopulatedValues();
      reset({ ...prePopulated, ...(restoredRef.current || {}) });
    }
  }, [userProfile]);

  // Resume where they backed out. The SSN digits were never saved.
  useEffect(() => {
    if (!resume) return;
    const saved = readDraft(userId);
    if (!saved) return;
    restoredRef.current = saved.values;
    reset({ ...getPrePopulatedValues(), ...saved.values });
    setRegistrationType(saved.registrationType);
    setRegisterForAgency(saved.registerForAgency);
    setCoversDownline(saved.coversDownline);
    setCompletedSteps(saved.completedSteps as WizardStep[]);
    setCurrentStep(saved.step as WizardStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveDraft = () => {
    if (submittedRef.current || !registrationType) return;
    writeDraft(userId, {
      step: currentStep,
      completedSteps: completedSteps as number[],
      registrationType,
      registerForAgency,
      coversDownline,
      values: getValues() as Record<string, any>,
    });
  };

  // Save as they go, so backing out (or closing the tab) doesn't lose anything.
  useEffect(() => {
    if (isSuccess || !registrationType) return;
    saveDraft();
    const sub = watch(() => {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = window.setTimeout(saveDraft, 400);
    });
    return () => {
      sub.unsubscribe();
      window.clearTimeout(saveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, completedSteps, registrationType, registerForAgency, coversDownline, isSuccess]);

  const handleFinishLater = () => {
    saveDraft();
    const typedSsn = !!(getValues("ssn") || "").trim();
    toast({
      title: "Progress saved",
      description: typedSsn
        ? "Resume any time from this tab. You'll re-enter your SSN digits when you do."
        : "Resume any time from this tab.",
    });
    onCancel?.();
  };

  // White-labels that charge per registration (MRFG): say so before Submit.
  const [feeApplies, setFeeApplies] = useState(false);
  useEffect(() => {
    a2pService
      .getRegistrationFee()
      .then((fee) => setFeeApplies(!!fee?.applies))
      .catch(() => setFeeApplies(false));
  }, []);

  /**
   * Validate current step before allowing navigation
   * Steps: 0=Type, 1=EIN, 2=CP575(LV only), 3=Business, 4=Campaign, 5=Payment
   */
  const validateCurrentStep = async (): Promise<boolean> => {
    let fieldsToValidate: (keyof A2PVerificationData)[] = [];

    switch (currentStep) {
      case 0: // Registration Type Selection
        if (!registrationType) {
          toast({
            title: "Choose a registration type",
            description: "Pick sole proprietor or Low Volume Standard to continue.",
            variant: "destructive",
          });
          return false;
        }
        return true;

      case 1: // EIN Check (only for Low Volume Standard)
        // For Sole Proprietor, this step is skipped
        if (registrationType === 'sole_proprietor') {
          return true;
        }
        if (hasEIN) {
          fieldsToValidate = ["hasEIN", "ein"];
        } else {
          // If no EIN, user can't proceed with Low Volume Standard
          toast({
            title: "An EIN is needed",
            description: "Low Volume Standard registers your business by its EIN. Without one, go back and choose sole proprietor.",
            variant: "destructive",
          });
          return false;
        }
        break;

      case 2: // CP 575 Upload (Low Volume Standard only)
        // Validate CP 575 upload and EIN age confirmation
        const cp575UploadId = getValues("cp575UploadId" as any);
        const einAgeConfirmed = getValues("einAgeConfirmed" as any);

        if (einAgeConfirmed === undefined) {
          toast({
            title: "Tell us your EIN's age",
            description: "Say whether your EIN is at least 45 days old.",
            variant: "destructive",
          });
          return false;
        }

        if (!cp575UploadId) {
          toast({
            title: "Upload your CP 575",
            description: "Upload the IRS letter that shows your EIN.",
            variant: "destructive",
          });
          return false;
        }
        return true;

      case 3: // Business Info / Personal Info (Sole Proprietor)
        if (registrationType === 'sole_proprietor') {
          // Sole Proprietor: Validate personal info with SSN
          fieldsToValidate = [
            "businessName", // Full Legal Name for Sole Proprietor
            "businessEmail",
            "businessPhone",
            "street",
            "city",
            "state",
            "zipCode",
          ];
          // Validate SSN separately (only last 4 digits required)
          const ssnValue = getValues("ssn");
          const ssnDigits = ssnValue?.replace(/\D/g, '') || '';
          if (ssnDigits.length !== 4) {
            toast({
              title: "Enter your SSN digits",
              description: "Enter the last 4 digits of your Social Security Number.",
              variant: "destructive",
            });
            return false;
          }
        } else {
          // Low Volume Standard: Validate business info
          fieldsToValidate = [
            "businessName",
            "businessType",
            "businessEmail",
            "businessPhone",
            "authorizedRepFirstName",
            "authorizedRepLastName",
            "street",
            "city",
            "state",
            "zipCode",
          ];
        }
        break;

      case 4: // Campaign Info
        fieldsToValidate = ["campaignName", "campaignDescription", "useCase"];

        // Validate sample messages (at least 2 required)
        const sampleMessages = getValues("sampleMessages") || [];
        const validMessages = sampleMessages.filter((msg) => msg.message && msg.message.trim().length > 0);

        if (validMessages.length < 2) {
          toast({
            title: "Add 2 sample messages",
            description: "The carriers need at least 2 examples of texts you'll send.",
            variant: "destructive",
          });
          return false;
        }
        break;

      case 5: // Payment (no validation needed)
        return true;

      default:
        return true;
    }

    const isValid = await trigger(fieldsToValidate);

    if (!isValid) {
      toast({
        title: "Check the highlighted fields",
        description: "A few fields need fixing before you continue.",
        variant: "destructive",
      });
    }

    return isValid;
  };

  /**
   * Handle next step navigation
   * Steps: 0=Type, 1=EIN, 2=CP575(LV only), 3=Business, 4=Campaign, 5=Payment
   */
  const handleNext = async () => {
    const isValid = await validateCurrentStep();

    if (!isValid) return;

    // Mark current step as completed
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }

    // After Step 4 (Campaign), check if payment is required
    if (currentStep === 4) {
      setCheckingPayment(true);
      try {
        const paymentCheck = await a2pService.checkPaymentRequirement();
        setPaymentRequired(paymentCheck.required);

        if (paymentCheck.required) {
          // Show payment step
          setCurrentStep(5 as WizardStep);
        } else {
          // Skip payment and submit directly
          await handleFinalSubmit();
        }
      } catch (error: any) {
        toast({
          title: "Couldn't continue",
          description: "We couldn't check whether a payment is needed. Try again.",
          variant: "destructive",
        });
      } finally {
        setCheckingPayment(false);
      }
      return;
    }

    // Special handling for Step 0 -> next step transition
    // For Sole Proprietor, skip Steps 1 (EIN) and 2 (CP575) and go directly to Step 3 (Business)
    if (currentStep === 0 && registrationType === 'sole_proprietor') {
      setCurrentStep(3 as WizardStep); // Skip to Business Info
      return;
    }

    // For Low Volume Standard from Step 1, go to Step 2 (CP575)
    // For Low Volume Standard from Step 2 (CP575), go to Step 3 (Business)
    // Regular progression for other steps

    // Move to next step
    setCurrentStep((prev) => (prev + 1) as WizardStep);
  };

  /**
   * Handle previous step navigation
   * Steps: 0=Type, 1=EIN, 2=CP575(LV only), 3=Business, 4=Campaign, 5=Payment
   */
  const handleBack = () => {
    // Special handling for Sole Proprietor going back from Step 3 (Business)
    // Should go back to Step 0 (skip Steps 1 and 2)
    if (currentStep === 3 && registrationType === 'sole_proprietor') {
      setCurrentStep(0 as WizardStep);
      return;
    }

    if (currentStep > 0) {
      setCurrentStep((prev) => (prev - 1) as WizardStep);
    }
  };

  /**
   * Handle payment completion
   */
  const handlePaymentComplete = async () => {
    try {
      // Create Stripe checkout session
      const session = await a2pPaymentService.createCheckoutSession();

      // Redirect to Stripe checkout
      if (!allowCheckout("payment")) return;
      window.location.href = session.sessionUrl;
    } catch (error: any) {

      toast({
        title: "Couldn't open checkout",
        description: error.message || "Try again.",
        variant: "destructive",
      });
    }
  };

  /**
   * Handle payment skip (for full_fledged users)
   */
  const handlePaymentSkip = async () => {
    await handleFinalSubmit();
  };

  /**
   * Submit verification data to backend
   */
  const handleFinalSubmit = async () => {
    // Prevent double-submit with synchronous ref check
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const formData = getValues();

      // Validate EIN format if provided
      if (formData.ein && !a2pService.validateEIN(formData.ein)) {
        toast({
          title: "Check your EIN",
          description: "Use the format 12-3456789.",
          variant: "destructive",
        });
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }

      // Filter out empty sample messages and extract message strings
      const validSampleMessages = (formData.sampleMessages || [])
        .filter((msg) => msg.message && msg.message.trim().length > 0)
        .map((msg) => msg.message);

      // Construct verification data with all required fields for the service
      // Use the selected registration type as the brand type
      const verificationData = {
        verificationType: 'independent' as const,
        brandType: registrationType || 'low_volume_standard',
        businessName: formData.businessName,
        businessType: registrationType === 'sole_proprietor' ? 'Sole Proprietorship' : formData.businessType,
        website: "https://insuracentral.com", // Always use InsuraCentral for verified privacy policy
        businessAddress: {
          street: formData.street,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country,
        },
        businessPhone: formData.businessPhone,
        businessEmail: formData.businessEmail,
        // Sole Proprietor uses SSN, Low Volume Standard uses EIN
        ...(registrationType === 'sole_proprietor'
          ? { ssn: formData.ssn }
          : {
              // Send the normalised form — the carrier wants XX-XXXXXXX, and
              // the field lets people type it however the IRS letter reads.
              ein: a2pService.normalizeEIN(formData.ein),
              taxId: a2pService.normalizeEIN(formData.ein),
            }
        ),
        authorizedRepFirstName: formData.authorizedRepFirstName,
        authorizedRepLastName: formData.authorizedRepLastName,
        campaignName: formData.campaignName,
        campaignDescription: formData.campaignDescription,
        // Sole prop texting number. '' = they skipped the pick -> backend auto-picks most-called.
        ...(registrationType === 'sole_proprietor' && (formData as any).preferredPhoneNumberId
          ? { preferredPhoneNumberId: (formData as any).preferredPhoneNumberId }
          : {}),
        useCase: formData.useCase,
        sampleMessages: validSampleMessages,
        volumeEstimate: formData.monthlyVolume || 6000,
        // CP 575 fields (for Low Volume Standard)
        ...(registrationType === 'low_volume_standard' && {
          cp575UploadId: (formData as any).cp575UploadId,
          einIsNew: (formData as any).einIsNew || false,
          einAgeConfirmed: (formData as any).einAgeConfirmed,
          // The agency itself is read from the caller's context server-side.
          ...(canRegisterForAgency && registerForAgency
            ? {
                registerForAgency: true,
                coversDownline,
                agencyRiskAccepted,
              }
            : {}),
        }),
      };

      // Submit verification - now goes to pending_review for manual admin approval
      let response;
      if (registrationType === 'low_volume_standard') {
        response = await a2pService.submitLowVolumeStandardWithCp575(verificationData as any);
      } else {
        // Sole Proprietor - also goes through manual review now
        response = await a2pService.submitVerification(verificationData);
      }

      // Map response to A2PVerificationStatus
      const verificationStatus: A2PVerificationStatus = {
        id: response.verificationId || "",
        userId: "", // Will be set by backend
        status: response.status as any,
        brandStatus: "pending",
        campaignStatus: "pending",
        twilioBrandId: response.brandSid,
        twilioCampaignId: response.campaignSid,
        retryCount: 0,
        paymentRequired: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        submittedAt: new Date().toISOString(),
      };

      setVerificationResult(verificationStatus);
      submittedRef.current = true;
      window.clearTimeout(saveTimer.current);
      clearDraft(userId);
      setIsSuccess(true);

      toast({
        title: "Registration submitted",
        description: response.message || "Our team will review it, usually within 1-2 business days.",
      });

      // Call onComplete callback if provided
      if (onComplete) {
        onComplete(verificationStatus);
      }
    } catch (error: any) {

      toast({
        title: "Couldn't submit",
        description: error.message || "Your details are still here. Try again.",
        variant: "destructive",
      });
      // Reset ref on error so user can retry
      isSubmittingRef.current = false;
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Render current step component
   * Steps: 0=Type, 1=EIN, 2=CP575(LV only), 3=Business, 4=Campaign, 5=Payment
   */
  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <Step0RegistrationTypeSelect
            selectedType={registrationType}
            onSelectType={setRegistrationType}
            canRegisterForAgency={canRegisterForAgency}
            agencyName={currentAgency?.agency?.name}
            registerForAgency={registerForAgency}
            onToggleRegisterForAgency={setRegisterForAgency}
            coversDownline={coversDownline}
            onChangeCoversDownline={setCoversDownline}
            riskAccepted={agencyRiskAccepted}
            onChangeRiskAccepted={setAgencyRiskAccepted}
          />
        );
      case 1:
        return <Step1EINCheck />;
      case 2:
        return <StepCp575Upload />;
      case 3:
        // Use different forms based on registration type
        return registrationType === 'sole_proprietor'
          ? <Step2SoleProprietorInfo />
          : <Step2BusinessInfo registrationType={registrationType} />;
      case 4:
        // Sole prop: let them choose the texting number here, the cheap moment to choose.
        // Skipping (leaving "Choose for me") keeps the existing most-called auto-pick.
        return (
          <div className="space-y-10">
            <Step3CampaignInfo registrationType={registrationType} />
            {registrationType === 'sole_proprietor' && <StepTextingNumber />}
          </div>
        );
      case 5:
        return (
          <PaymentStep
            onPaymentComplete={handlePaymentComplete}
            onSkip={handlePaymentSkip}
          />
        );
      default:
        return null;
    }
  };

  // Show success state after submission
  if (isSuccess && verificationResult) {
    return (
      <div className="space-y-10">
        <StatusPanel
          tone="waiting"
          label="In review"
          title="Your registration is in review"
          facts={
            verificationResult.id
              ? [{ label: "Registration ID", value: <span className="break-all font-mono text-[13px] font-normal">{verificationResult.id}</span>, wide: true }]
              : undefined
          }
        >
          <p>
            Our team reviews your details{registrationType === 'low_volume_standard' ? ' and CP 575 letter' : ''}, usually
            within 1-2 business days, and we&apos;ll notify you once it&apos;s approved.
          </p>
        </StatusPanel>
        <Panel className="px-4 py-5 sm:px-6">
          <ReviewSteps
            steps={[
              { key: "submitted", title: "Submitted", state: "done" },
              { key: "ours", title: "Our review", detail: "Usually 1-2 business days.", state: "current" },
              { key: "carriers", title: "Carrier review", detail: "Usually under a week, up to 2-3 weeks when busy.", state: "upcoming" },
              { key: "ready", title: "Ready to text", detail: "Your numbers are linked automatically.", state: "upcoming" },
            ]}
          />
        </Panel>
        {registrationType === 'low_volume_standard' && (
          <p className="text-sm text-neutral-500">We delete your CP 575 letter once your registration is approved.</p>
        )}
      </div>
    );
  }

  const navButton = cn("h-11 w-full sm:w-auto", focusRing);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleNext)} className="space-y-8">
        {/* Progress shows from step 1; the path isn't known until a type is chosen. */}
        {currentStep > 0 && currentStep <= 4 && (
          <WizardProgress
            currentStep={currentStep}
            completedSteps={completedSteps as number[]}
            registrationType={registrationType}
            action={
              onCancel ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleFinishLater}
                  disabled={isSubmitting || checkingPayment}
                  className={cn("-mr-2 h-9 text-neutral-600", focusRing)}
                >
                  Finish later
                </Button>
              ) : null
            }
          />
        )}

        <div className="min-h-[420px]">{renderStep()}</div>

        {currentStep === 4 && feeApplies && (
          <p className="rounded-lg bg-neutral-50 px-4 py-3 text-sm leading-relaxed text-neutral-700 ring-1 ring-inset ring-neutral-200">
            A $20 registration fee applies. You&apos;ll pay it right after you submit, and our team reviews your
            registration once it&apos;s paid.
          </p>
        )}

        <div className="flex flex-col-reverse gap-3 border-t border-neutral-200 pt-6 sm:flex-row sm:items-start sm:justify-between">
          <div>
            {currentStep === 0 && onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting || checkingPayment}
                className={cn(navButton, "sm:h-10")}
              >
                <ChevronLeft />
                Back to overview
              </Button>
            )}
            {currentStep > 0 && currentStep < 5 && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                disabled={isSubmitting || checkingPayment}
                className={cn(navButton, "sm:h-10")}
              >
                <ChevronLeft />
                Back
              </Button>
            )}
          </div>

          <div className="flex flex-col items-stretch gap-2 sm:items-end">
            {currentStep < 4 && (
              <Button
                type="button"
                size="lg"
                onClick={handleNext}
                disabled={
                  isSubmitting ||
                  checkingPayment ||
                  (currentStep === 0 && (!registrationType || agencyAckMissing))
                }
                className={cn(navButton, "px-6")}
              >
                Continue
                <ChevronRight />
              </Button>
            )}

            {currentStep === 4 && (
              <Button
                type="button"
                size="lg"
                onClick={handleNext}
                disabled={isSubmitting || checkingPayment}
                className={cn(navButton, "px-6")}
              >
                {checkingPayment ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Checking…
                  </>
                ) : paymentRequired ? (
                  <>
                    Continue to payment
                    <ChevronRight />
                  </>
                ) : (
                  "Submit for review"
                )}
              </Button>
            )}

            {/* Say why Continue is off instead of leaving a dead button. */}
            {currentStep === 0 && registrationType && agencyAckMissing && (
              <p className="text-xs text-neutral-500">Tick &ldquo;I accept the shared risk&rdquo; to continue.</p>
            )}
            {/* Payment step buttons are handled by PaymentStep component */}
          </div>
        </div>

        {isSubmitting && (
          <div role="status" aria-live="polite" className="fixed inset-0 z-50 flex items-center justify-center bg-white/85 p-4">
            <div className="flex items-center gap-3 rounded-xl border border-neutral-200/80 bg-white px-5 py-4 text-sm font-medium text-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_16px_40px_-24px_rgba(16,24,40,0.18)]">
              <Loader2 aria-hidden className="size-4 animate-spin text-neutral-500" />
              Submitting your registration…
            </div>
          </div>
        )}
      </form>
    </FormProvider>
  );
}
